import { decodeJwtPayload } from 'utils/jwt';

import { getSessionRealm } from '@/services/auth/sessionRealm';

const UNIT_ID_CLAIM_KEYS = ['custom:unit_id', 'custom:unitId', 'unit_id', 'unitId'] as const;

const readTrimmedStringClaim = (token: string | null, claim: string): string | null => {
  if (!token?.trim()) {
    return null;
  }

  const payload = decodeJwtPayload(token);
  const raw = payload?.[claim];
  if (typeof raw !== 'string') {
    return null;
  }

  const trimmed = raw.trim();
  return trimmed || null;
};

/** Unit id from id token (tries Cognito custom claims and plain keys). */
export const getJwtUnitId = (token: string | null): string | null => {
  for (const claim of UNIT_ID_CLAIM_KEYS) {
    const value = readTrimmedStringClaim(token, claim);
    if (value) {
      return value;
    }
  }

  return null;
};

/** True when token identifies an end-user employee (scoped to one unit). */
export const isEmployeeJwtUser = (token: string | null): boolean => {
  const userType =
    readTrimmedStringClaim(token, 'custom:userType') ??
    readTrimmedStringClaim(token, 'custom:user_type');
  return userType?.toLowerCase() === 'employee';
};

/**
 * When the id token carries `custom:unit_id`, API paths must use it.
 * URL/localStorage (e.g. Unit Alpha / d410c96c…) can be stale → CROSS_UNIT_ACCESS_DENIED.
 * Org admins without that claim keep route/query/localStorage unit id.
 */
export const resolveOrganizationUnitApiUnitId = (
  routeUnitId: string,
  idToken: string | null,
  storeUnitId?: string | null
): string => {
  // Admin org UI uses the unit from the URL; EU employee tokens must not override it.
  if (getSessionRealm() === 'admin') {
    return routeUnitId;
  }

  const jwtUnitId = getJwtUnitId(idToken);
  if (jwtUnitId) {
    return jwtUnitId;
  }

  const fromStore = storeUnitId?.trim();
  if (fromStore) {
    return fromStore;
  }

  return routeUnitId;
};
